import React, { useState, useRef, useEffect } from "react";
import {
  toastSuccess,
  toastError,
  toastInfo,
} from "../utils/toast";
import { useMessageAction } from "../hooks/useMessageAction";
import { useScheduleAction } from "../hooks/useScheduleAction";

// ─── Centralised helpers (mirror of ChatWindow.js) ─────────────────────────
function resolveMsgId(msg) {
  return (
    msg?.id ||
    msg?.messageId ||
    msg?.raw?.key?.msgId
  );
}

function resolveSenderChatId(msg) {
  return (
    msg?.senderChatId ||
    msg?.raw?.key?.senderChatId
  );
}

/* ─── WhatsApp-accurate SVG icons ────────────────────────────────────────── */
const Icons = {
  Reply: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
    </svg>
  ),
  Forward: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />
    </svg>
  ),
  Copy: () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z" />
    </svg>
  ),
  Delete: () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  ),
  Close: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  ),
  MoreVert: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  ),
};

/* ─── Reusable icon button ───────────────────────────────────────────────── */
function ActionBtn({ onClick, title, children, danger = false, disabled = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`
        flex flex-col items-center justify-center gap-0.5
        p-2 rounded-full transition-colors
        ${danger
          ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
          : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }
        disabled:opacity-40 disabled:pointer-events-none
      `}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   📌 PIN DURATION PICKER MODAL
─────────────────────────────────────────────────────────────────────────────── */
function PinDurationModal({ onConfirm, onClose }) {
  const options = [
    { key: "24h", label: "24 hours" },
    { key: "7d",  label: "7 days"   },
    { key: "30d", label: "30 days"  },
    { key: "60d", label: "60 days"  },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xs rounded-2xl bg-white dark:bg-neutral-900 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3">
          <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            Pin message for…
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Everyone in this chat will see the pinned message.
          </p>
        </div>
        <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
        <div className="py-1">
          {options.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onConfirm(key)}
              className="w-full px-5 py-3.5 text-left text-sm text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors touch-manipulation"
            >
              {label}
            </button>
          ))}
          <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
          <button
            onClick={onClose}
            className="w-full px-5 py-3.5 text-left text-sm text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors touch-manipulation"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SelectionActionBar({
  selectionMode,
  selectedMessages,
  selectedItem,
  messages,
  setMessages,
  getUserKey,
  getGroupKey,
  socket,
  myChatId,
  clearSelection,
  showToast,
  setSelectedReply,
  onForwardMessages,
  groupMembers,
  onStartEdit,
  onScheduleSelected,
  onStartScheduleEdit,
  setUsers,    // ← forwarded to useScheduleAction so sidebar preview updates on delete/edit
  setGroups,   // ← forwarded to useScheduleAction so sidebar preview updates on delete/edit
  allMessages, // ← full messages map for cross-chat batch ops
}) {
  const [showDeleteModal,  setShowDeleteModal]  = useState(false);
  const [showPinModal,     setShowPinModal]      = useState(false);
  const [showMoreMenu,     setShowMoreMenu]      = useState(false);
  const [isProcessing,     setIsProcessing]      = useState(false);

  const moreMenuRef = useRef(null);

  // Close the more-menu when clicking outside
  useEffect(() => {
    if (!showMoreMenu) return;
    const handler = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showMoreMenu]);

  // ✅ Hooks called unconditionally before any early return
 const { deleteMessages, pinMessage, unpinMessage } = useMessageAction({
  socket,
  myChatId,
  selectedItem,
  setMessages,
  getUserKey,
  getGroupKey,
  setUsers,       // ← fixes "a is not a function" on sidebar update
  setGroups,      // ← fixes "a is not a function" on sidebar update
  groupMembers,   // ← needed for tombstone role detection in groups
});

  const { deleteSchedule, editSchedule } = useScheduleAction({
    socket,
    myChatId,
    selectedItem,
    setMessages,
    getUserKey,
    getGroupKey,
    allMessages,
    setUsers,   // ← NEW: sidebar preview cleared on delete
    setGroups,  // ← NEW: sidebar preview cleared on delete
  });

  useEffect(() => {
  if (!selectionMode) {
    setShowDeleteModal(false);   // ✅ force close when exiting selection mode
  }
}, [selectionMode]);

  // ✅ Early return AFTER all hook calls
  if (!selectionMode) return null;

  /* ── Message list for this chat ── */
  const key =
    selectedItem.type === "group"
      ? getGroupKey(selectedItem.groupId)
      : getUserKey(selectedItem.chatId);

  const list  = messages[key] || [];
  const count = selectedMessages.length;

  /* ── Helper: find a message object by the selected id ── */
  const findMsg = (id) => list.find((mm) => resolveMsgId(mm) === id);

  /* ── Detect if ALL selected messages are scheduled ── */
  const allScheduled = selectedMessages.every((id) => {
    const m = findMsg(id);
    return m && !!m.scheduledAt;
  });

  /* ── For single selection: is this one message scheduled? ── */
  const singleMsgId = count === 1 ? selectedMessages[0] : null;
  const singleMsg   = singleMsgId ? findMsg(singleMsgId) : null;
  const isScheduled = !!singleMsg?.scheduledAt;

  /* ── Permission checks ── */
  const allSentByMe = selectedMessages.every((msgId) => {
    const m = findMsg(msgId);
    if (!m) return false;
    return String(resolveSenderChatId(m)) === String(myChatId);
  });

  let amAdmin = false;
  if (selectedItem.type === "group") {
    const members = groupMembers?.[selectedItem.groupId] || [];
    const me = members.find((m) => String(m.chatId) === String(myChatId));
    amAdmin = me?.isAdmin || me?.isSuperAdmin;
  }

  const canDeleteForEveryone =
    selectedItem.type === "group" ? amAdmin || allSentByMe : allSentByMe;

  const canEdit = count === 1 && allSentByMe;

  const canPin =
    count === 1 &&
    !isScheduled &&
    (selectedItem.type === "user" || amAdmin);

  const canSchedule = count === 1 && allSentByMe && !isScheduled;

  let isAlreadyPinned = false;

  if (singleMsgId) {
    const msg = findMsg(singleMsgId);
    if (msg) {
      if (selectedItem.type === "group") {
        isAlreadyPinned = !!msg.pinnedBy;
      } else {
        isAlreadyPinned = msg.pinnedBy?.includes(myChatId);
      }
    }
  }

  /* ── Handlers ── */
  const handleCopy = async () => {
    const texts = selectedMessages
      .map((id) => {
        const m = findMsg(id);
        return m?.text || m?.raw?.message?.conversation || "";
      })
      .filter(Boolean);
    if (!texts.length) { toastInfo("Nothing to copy"); return; }
    await navigator.clipboard.writeText(texts.join("\n"));
    toastSuccess("Copied to clipboard");
    clearSelection();
  };

  const handleReply = () => {
    if (count !== 1) { toastInfo("Select one message to reply"); return; }
    const msgId = selectedMessages[0];
    const msg   = findMsg(msgId);
    if (!msg) return;
    if (msg.scheduledAt) { toastInfo("Cannot reply to a scheduled message"); return; }
    setSelectedReply({
      msgId,
      text:  msg.text || msg.raw?.message?.conversation || "",
      media: msg.media || msg.raw?.message?.media || null,
    });
    clearSelection();
  };

  const handleForward = () => {
    const payload = selectedMessages
      .map((id) => findMsg(id))
      .filter((m) => m && !m.scheduledAt);
    if (!payload.length) { toastInfo("Nothing to forward"); return; }
    onForwardMessages(payload);
    clearSelection();
  };

  /* ── Delete: batchId forwarded → useScheduleAction refreshes sidebar ── */
 const doDelete = ({ forEveryone = false }) => {
  if (isProcessing) return;

  setShowDeleteModal(false);   // ✅ CLOSE FIRST
  setIsProcessing(true);

  try {
    if (allScheduled) {
      selectedMessages.forEach((msgId) => {
        const m = findMsg(msgId);
        deleteSchedule({ msgId, batchId: m?.batchId ?? null });
      });
    } else {
      deleteMessages({ messageIds: selectedMessages, forEveryone });
    }

    clearSelection();
  } finally {
    setIsProcessing(false);
  }
};
  /* ── Edit: batchId + scheduledAt forwarded → useScheduleAction updates sidebar ── */
  const openEdit = () => {
    setShowMoreMenu(false);
    if (count !== 1) { toastInfo("Select exactly one message to edit"); return; }
    const msgId = selectedMessages[0];
    const msg   = findMsg(msgId);
    if (!msg) return;
    if (String(resolveSenderChatId(msg)) !== String(myChatId)) {
      toastError("You can only edit your own messages"); return;
    }

    const currentText = msg.text || msg.raw?.message?.conversation || "";

    if (msg.scheduledAt) {
      onStartScheduleEdit?.({
        msgId,
        currentText,
        scheduledAt: msg.scheduledAt,
        batchId: msg.batchId ?? null,
      });
    } else {
      onStartEdit?.({ msgId, currentText });
    }

    clearSelection();
  };

  /* ── Schedule ── */
  const openSchedule = () => {
    setShowMoreMenu(false);
    if (count !== 1) { toastInfo("Select exactly one message to schedule"); return; }
    const msgId = selectedMessages[0];
    const msg   = findMsg(msgId);
    if (!msg) return;
    if (String(resolveSenderChatId(msg)) !== String(myChatId)) {
      toastError("You can only schedule your own messages"); return;
    }
    onScheduleSelected?.({
      msgId,
      text:  msg.text  || msg.raw?.message?.conversation || "",
      media: msg.media || msg.raw?.message?.media        || null,
    });
    clearSelection();
  };

  /* ── 📌 Pin / Unpin ── */
  const handlePinClick = () => {
    setShowMoreMenu(false);
    if (isAlreadyPinned) {
      unpinMessage({ msgId: singleMsgId });
      showToast?.("Message unpinned");
      clearSelection();
    } else {
      setShowPinModal(true);
    }
  };

  const confirmPin = (duration) => {
    pinMessage({ msgId: singleMsgId, duration });
    showToast?.("Message pinned");
    setShowPinModal(false);
    clearSelection();
  };

  const hasMoreMenuItems = canPin || canEdit || canSchedule;

  const deleteModalTitle = allScheduled
    ? `Cancel ${count} scheduled message${count !== 1 ? "s" : ""}?`
    : `Delete ${count} message${count !== 1 ? "s" : ""}?`;

  const deleteModalBody = allScheduled
    ? "The scheduled message will be removed and never sent."
    : "This action cannot be undone.";

  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          SELECTION ACTION BAR
      ══════════════════════════════════════════════════════════ */}
      <div
        className="
          absolute top-0 inset-x-0 z-40
          flex items-center
          h-14
          px-2
          bg-white dark:bg-neutral-900
          text-neutral-900 dark:text-neutral-100
          border-b border-neutral-200 dark:border-neutral-800
          shadow-sm
        "
      >
        {/* LEFT — close + count */}
        <button
          onClick={clearSelection}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          title="Cancel selection"
        >
          <Icons.Close />
        </button>

        <span className="ml-1 text-base font-medium tabular-nums select-none">
          {count}
        </span>

        {/* RIGHT — actions */}
        <div className="ml-auto flex items-center">
          {count === 1 && !isScheduled && (
            <ActionBtn title="Reply" onClick={handleReply}>
              <Icons.Reply />
            </ActionBtn>
          )}

          {!allScheduled && (
            <ActionBtn title="Forward" onClick={handleForward}>
              <Icons.Forward />
            </ActionBtn>
          )}

          <ActionBtn title="Copy" onClick={handleCopy}>
            <Icons.Copy />
          </ActionBtn>

          <ActionBtn title="Delete" danger onClick={() => setShowDeleteModal(true)}>
            <Icons.Delete />
          </ActionBtn>

          {/* ── 3-dot overflow menu ── */}
          <div className="relative" ref={moreMenuRef}>
            <ActionBtn
              title="More options"
              onClick={() => setShowMoreMenu((s) => !s)}
            >
              <Icons.MoreVert />
            </ActionBtn>

            {showMoreMenu && (
              <div className="absolute right-0 top-11 z-50 min-w-[160px] rounded-xl overflow-hidden bg-white dark:bg-[#233138] shadow-[0_4px_24px_rgba(0,0,0,0.18)] border border-black/5 dark:border-white/5">

                {canEdit && (
                  <button
                    onClick={openEdit}
                    className="w-full text-left px-4 py-3 sm:py-[10px] text-[13.5px] text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors touch-manipulation flex items-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="shrink-0 opacity-70">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                    </svg>
                    {isScheduled ? "Edit scheduled text" : "Edit"}
                  </button>
                )}

                {canEdit && canPin && (
                  <div className="h-px bg-neutral-100 dark:bg-neutral-700 mx-3" />
                )}

                {canPin && (
                  <button
                    onClick={handlePinClick}
                    className="w-full text-left px-4 py-3 sm:py-[10px] text-[13.5px] text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors touch-manipulation flex items-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="shrink-0 opacity-70">
                      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                    </svg>
                    {isAlreadyPinned ? "Unpin message" : "Pin message"}
                  </button>
                )}

                {(canEdit || canPin) && canSchedule && (
                  <div className="h-px bg-neutral-100 dark:bg-neutral-700 mx-3" />
                )}

                {canSchedule && (
                  <button
                    onClick={openSchedule}
                    className="w-full text-left px-4 py-3 sm:py-[10px] text-[13.5px] text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors touch-manipulation flex items-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="shrink-0 opacity-70">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
                    </svg>
                    Schedule send
                  </button>
                )}

                {!hasMoreMenuItems && (
                  <div className="px-4 py-3 text-[13px] text-neutral-400 dark:text-neutral-500 select-none">
                    {count > 1
                      ? "Select one message to see more options"
                      : "Only admins can pin in groups"}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ── */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
          onClick={() => !isProcessing && setShowDeleteModal(false)}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-white dark:bg-neutral-900 overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3">
              <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                {deleteModalTitle}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                {deleteModalBody}
              </p>
            </div>
            <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
            <div className="py-1">
              {allScheduled ? (
                <button
                  onClick={() => doDelete({ forEveryone: false })}
                  disabled={isProcessing}
                  className="w-full px-5 py-3.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 disabled:opacity-40 transition-colors touch-manipulation"
                >
                  Cancel scheduled message{count !== 1 ? "s" : ""}
                </button>
              ) : (
                <>
                  {canDeleteForEveryone && (
                   <button
  onClick={(e) => {
    e.stopPropagation(); // ✅ FIX: prevent modal overlay conflict
    doDelete({ forEveryone: true });
  }}
  disabled={isProcessing}
  className="w-full px-5 py-3.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 disabled:opacity-40 transition-colors"
>
  Delete for everyone
</button>
                  )}
                 <button
  onClick={(e) => {
    e.stopPropagation(); // ✅ FIX
    doDelete({ forEveryone: false });
  }}
  disabled={isProcessing}
  className="w-full px-5 py-3.5 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 disabled:opacity-40 transition-colors"
>
  Delete for me
</button>
                </>
              )}
              <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isProcessing}
                className="w-full px-5 py-3.5 text-left text-sm text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 📌 PIN DURATION MODAL ── */}
      {showPinModal && (
        <PinDurationModal
          onConfirm={confirmPin}
          onClose={() => setShowPinModal(false)}
        />
      )}
    </>
  );
}